import { useQuery } from "@tanstack/react-query";
import { ChainOfCustodyAPI } from "./coc.api";
import {
  ChainOfCustodyFormValues,
  EvidenceSelectionState,
} from "./coc-api.types";
import EvidenceCocAPI from "@/api/evidence/coc.js";
import type { CocRecord } from "./Coc.types";

const useGetBulkUpdateTotal = (
  data: {
    coc_type?: ChainOfCustodyFormValues["type"];
    selectionState: EvidenceSelectionState;
  },
  options?: { enabled?: boolean }
) => {
  return useQuery({
    queryKey: ["coc", "get-bulk-update-total", data],
    queryFn: () => ChainOfCustodyAPI.getBulkUpdateTotal(data),
    ...options,
  });
};

const useGetCocRecords = (
  evidenceId: number | string | undefined,
  options?: {
    enabled?: boolean;
    staleTime?: number;
    refetchOnWindowFocus?: boolean;
    retry?: boolean | number;
  }
) => {
  // Spread only the keys the caller actually passed. TanStack merges options
  // with object spread, so an explicit `refetchOnWindowFocus: undefined` would
  // override the QueryClient defaultOptions instead of falling through to them.
  const { enabled, ...passthrough } = options ?? {};

  return useQuery<CocRecord[]>({
    queryKey: ["evidence:cocData", { evidence_id: evidenceId }],
    queryFn: async () => {
      const records = await EvidenceCocAPI.getCocRecords({
        evidence_id: evidenceId,
      } as any);
      // connection.js resolves API failures as { success: false, error }
      // instead of rejecting; without this guard that object would be cached
      // as data and crash consumers expecting an array.
      if (!Array.isArray(records)) {
        throw new Error(
          `Failed to fetch chain of custody records for evidence ${evidenceId}`
        );
      }
      return records as CocRecord[];
    },
    enabled: evidenceId != null && (enabled ?? true),
    // Failed requests already toast via the connection.js interceptor, once
    // per attempt — don't retry unless a caller opts in.
    retry: false,
    ...passthrough,
  });
};

export const CocQuery = {
  useGetBulkUpdateTotal,
  useGetCocRecords,
};
